import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Code, Settings, Crown } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

const CCSPEmbedDocs = () => {
  const { toast } = useToast();
  const [copied, setCopied] = useState<string | null>(null);

  const baseUrl = window.location.origin;
  const embedUrl = `${baseUrl}/#/ccsp-embed`;

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast({
      title: "Copied!",
      description: `${type} code copied to clipboard`,
    });
    setTimeout(() => setCopied(null), 2000);
  };

  const embedCodes = [
    {
      title: "Full C-CSP Page",
      description: "Complete C-CSP certification page with all sections",
      code: `<iframe 
  src="${embedUrl}" 
  width="100%" 
  height="1400" 
  frameborder="0" 
  scrolling="auto"
  title="C-CSP Certification - Full Page">
</iframe>`,
      preview: `${embedUrl}`
    },
    {
      title: "Compact Version",
      description: "Condensed C-CSP page for smaller spaces",
      code: `<iframe 
  src="${embedUrl}?compact=true" 
  width="100%" 
  height="1000" 
  frameborder="0" 
  scrolling="auto"
  title="C-CSP Certification - Compact">
</iframe>`,
      preview: `${embedUrl}?compact=true`
    },
    {
      title: "Quick Facts Only",
      description: "Just the key C-CSP certification facts",
      code: `<iframe 
  src="${embedUrl}?hero=false&learning=false&eligibility=false&framework=false&application=false&ideal=false&steps=false" 
  width="100%" 
  height="300" 
  frameborder="0" 
  scrolling="auto"
  title="C-CSP Quick Facts">
</iframe>`,
      preview: `${embedUrl}?hero=false&learning=false&eligibility=false&framework=false&application=false&ideal=false&steps=false`
    },
    {
      title: "Elite Expertise Focus",
      description: "Highlights elite-level competencies and leadership areas",
      code: `<iframe 
  src="${embedUrl}?hero=false&facts=false&eligibility=false&framework=false&application=false&ideal=false&steps=false" 
  width="100%" 
  height="600" 
  frameborder="0" 
  scrolling="auto"
  title="C-CSP Elite Expertise">
</iframe>`,
      preview: `${embedUrl}?hero=false&facts=false&eligibility=false&framework=false&application=false&ideal=false&steps=false`
    },
    {
      title: "Eligibility & Application",
      description: "Shows eligibility criteria and application process",
      code: `<iframe 
  src="${embedUrl}?hero=false&facts=false&learning=false&framework=false&ideal=false&steps=false" 
  width="100%" 
  height="800" 
  frameborder="0" 
  scrolling="auto"
  title="C-CSP Eligibility & Application">
</iframe>`,
      preview: `${embedUrl}?hero=false&facts=false&learning=false&framework=false&ideal=false&steps=false`
    },
    {
      title: "Framework Requirements",
      description: "Implementation requirements and documentation needed",
      code: `<iframe 
  src="${embedUrl}?hero=false&facts=false&learning=false&eligibility=false&application=false&ideal=false&steps=false" 
  width="100%" 
  height="500" 
  frameborder="0" 
  scrolling="auto"
  title="C-CSP Framework Requirements">
</iframe>`,
      preview: `${embedUrl}?hero=false&facts=false&learning=false&eligibility=false&application=false&ideal=false&steps=false`
    }
  ];

  const parameters = [
    {
      param: "compact",
      type: "boolean",
      default: "false",
      description: "Reduces spacing, font sizes, and content for smaller displays"
    },
    {
      param: "hero",
      type: "boolean", 
      default: "true",
      description: "Shows/hides the main hero section with C-CSP badge and title"
    },
    {
      param: "facts",
      type: "boolean",
      default: "true", 
      description: "Shows/hides the quick facts cards (fee, duration, etc.)"
    },
    {
      param: "learning",
      type: "boolean",
      default: "true",
      description: "Shows/hides the elite-level expertise competencies"
    },
    {
      param: "eligibility",
      type: "boolean",
      default: "true",
      description: "Shows/hides the eligibility criteria section"
    },
    {
      param: "framework",
      type: "boolean",
      default: "true",
      description: "Shows/hides the framework implementation requirements"
    },
    {
      param: "application",
      type: "boolean",
      default: "true",
      description: "Shows/hides the application process and fee structure"
    },
    {
      param: "ideal",
      type: "boolean",
      default: "true",
      description: "Shows/hides the ideal candidates section"
    },
    {
      param: "steps",
      type: "boolean",
      default: "true",
      description: "Shows/hides the next steps and payment options"
    },
    {
      param: "width",
      type: "string",
      default: "6xl",
      description: "Container max-width (sm, md, lg, xl, 2xl, 3xl, 4xl, 5xl, 6xl, 7xl)"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <Crown className="w-8 h-8" />
            <h1 className="text-4xl md:text-5xl font-bold">
              C-CSP Embed Code Generator
            </h1>
          </div>
          <p className="text-xl text-purple-100 max-w-3xl">
            Integrate the C-CSP certification page into your website with customizable sections and layouts
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Quick Start */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              C-CSP Embed Options
            </CardTitle>
            <CardDescription>
              Choose from these pre-configured embed codes for different use cases
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6">
              {embedCodes.map((embed, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{embed.title}</h3>
                      <p className="text-sm text-slate-600">{embed.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(embed.preview, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(embed.code, embed.title)}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        {copied === embed.title ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                  <pre className="bg-slate-100 p-3 rounded text-sm overflow-x-auto">
                    <code>{embed.code}</code>
                  </pre>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Parameters Reference */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>C-CSP Embed Parameters</CardTitle>
            <CardDescription>
              Customize the C-CSP embed using these URL parameters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Parameter</th>
                    <th className="text-left p-3 font-semibold">Type</th>
                    <th className="text-left p-3 font-semibold">Default</th>
                    <th className="text-left p-3 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {parameters.map((param, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-3">
                        <Badge variant="outline">{param.param}</Badge>
                      </td>
                      <td className="p-3 text-sm text-slate-600">{param.type}</td>
                      <td className="p-3 text-sm font-mono">{param.default}</td>
                      <td className="p-3 text-sm">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Usage Examples */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Common Use Cases</CardTitle>
            <CardDescription>
              Popular C-CSP embed configurations for different scenarios
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Executive Portal</h3>
                <p className="text-sm text-slate-600">
                  Elite expertise and eligibility criteria for executive audiences
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?facts=false&framework=false&application=false&ideal=false&steps=false&compact=true`}</code>
                </pre>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold">Professional Development</h3>
                <p className="text-sm text-slate-600">
                  Full C-CSP page for career advancement planning
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?width=7xl`}</code>
                </pre>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Application Widget</h3>
                <p className="text-sm text-slate-600">
                  Application process and payment options only
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?compact=true&hero=false&learning=false&eligibility=false&framework=false&ideal=false&width=sm`}</code>
                </pre>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Requirements Summary</h3>
                <p className="text-sm text-slate-600">
                  Eligibility and framework requirements for qualified professionals
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?hero=false&facts=false&learning=false&application=false&ideal=false&steps=false&width=4xl`}</code>
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Notes */}
        <Card>
          <CardHeader>
            <CardTitle>C-CSP Embed Technical Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Recommended Heights</h4>
                <ul className="text-slate-600 space-y-1 ml-4">
                  <li>• Full Page: 1400px</li>
                  <li>• Compact: 1000px</li>
                  <li>• Quick Facts Only: 300px</li>
                  <li>• Elite Expertise: 600px</li>
                  <li>• Eligibility & Application: 800px</li>
                  <li>• Framework Requirements: 500px</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Payment Integration</h4>
                <p className="text-slate-600">
                  C-CSP embeds include integrated SumUp payment buttons for both application (€50) and final (€450) fees, providing seamless payment processing for qualified candidates.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Responsive Design</h4>
                <p className="text-slate-600">
                  All C-CSP embeds are fully responsive and adapt to container width. The compact mode is especially optimized for executive dashboards and professional development portals.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Brand Consistency</h4>
                <p className="text-slate-600">
                  The embed maintains C-CSP's purple/indigo premium colors and includes the crown icon for visual consistency and authority, reflecting the elite nature of this chartered designation.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Eligibility Verification</h4>
                <p className="text-slate-600">
                  The embed emphasizes the importance of eligibility verification before application, encouraging candidates to contact administration for pre-qualification assessment.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CCSPEmbedDocs;